import { useEffect, useRef } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button } from '@jumpstone/ui-react';
import { WIZARD_STEPS, step_state } from './pay_run_calculations';
import { WIZARD_STEP_KEY } from '../../lib/label_keys';
import { format_number } from '../../lib/format';

/**
 * The run-payroll wizard's numbered step rail. Moved out of
 * `run_payroll_screen.tsx` unchanged (markup, classes and scroll behaviour)
 * so the screen stays a composition root.
 */
export function PayRunStepRail({
  step,
  on_select,
}: {
  step: number;
  on_select: (step: number) => void;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const current_step_ref = useRef<HTMLLIElement | null>(null);

  // Keep the active step visible in the horizontally-scrolling step rail
  // (phones can't see every step at once). Guarded for jsdom, which doesn't
  // implement scrollIntoView, and for prefers-reduced-motion.
  useEffect(() => {
    const el = current_step_ref.current;
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    const prefers_reduced_motion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
      behavior: prefers_reduced_motion ? 'auto' : 'smooth',
    });
  }, [step]);

  return (
    <ol className="wp-step-rail flex items-center gap-1 mb-5 overflow-x-auto no-scrollbar list-none p-0">
      {WIZARD_STEPS.map((id, index) => {
        const number = index + 1;
        const state = step_state(number, step);
        return (
          <li
            key={id}
            ref={state === 'current' ? current_step_ref : undefined}
            className="flex items-center gap-1"
          >
            <Button
              variant="ghost"
              size="sm"
              aria-current={state === 'current' ? 'step' : undefined}
              onClick={() => {
                on_select(number);
              }}
            >
              <span
                className={`wp-step-bubble ${
                  state === 'current'
                    ? 'is-current'
                    : state === 'done'
                      ? 'is-done'
                      : ''
                }`}
                aria-hidden="true"
              >
                {format_number(number, locale)}
              </span>
              <span
                className={`wp-step-label ${
                  state === 'current'
                    ? 'font-semibold'
                    : 'text-[color:var(--ink-muted)]'
                }`}
              >
                {t(WIZARD_STEP_KEY[id])}
              </span>
            </Button>
            {number < WIZARD_STEPS.length && (
              <span className="wp-step-arrow" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
